// useMeta — tiny replacement for react-helmet-async.
//
// M5.3. We have exactly 3 consumers (the browse index + 2 browse
// detail pages) for SEO-friendly <title>/<meta>/<og:> tags. Pulling
// in react-helmet-async + its provider for that surface area is more
// dependency weight than the feature deserves; this hook writes the
// tags directly on the document via useEffect, ~30 lines.
//
// Reset behavior: every property the hook writes is restored to its
// pre-render value when the component unmounts (or the props change),
// so navigating away from a browse page doesn't leave stale tags on
// the next route. This is what helmet does under the hood too.
//
// Not SSR-friendly. Real SEO indexing requires SSR/prerender, which
// is out of scope per the M5.3 prompt; the shareable-link previews
// (OG/Twitter card scrapers) DO see these tags via their client-side
// rendering paths, which is the win we're after.

import { useEffect } from 'react'

interface MetaOptions {
  title?: string
  description?: string
  // Open Graph + Twitter card defaults derived from title/description
  // when not explicitly set. Pass empty string to suppress.
  ogTitle?: string
  ogDescription?: string
  // Canonical URL (<link rel="canonical">). Optional. When provided,
  // writes the link tag and restores on unmount. Callers should drop
  // query strings before passing — filter state shouldn't fragment
  // indexing.
  canonical?: string
}

function setMeta(name: string, attr: 'name' | 'property', value: string): () => void {
  let el = document.head.querySelector(`meta[${attr}="${name}"]`) as
    | HTMLMetaElement
    | null
  const existed = el !== null
  const prevContent = el?.getAttribute('content') ?? null
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, name)
    document.head.appendChild(el)
  }
  el.setAttribute('content', value)
  return () => {
    if (!el) return
    if (existed) {
      // Restore the previous content (or remove the attribute if it
      // wasn't present before).
      if (prevContent !== null) el.setAttribute('content', prevContent)
      else el.removeAttribute('content')
    } else {
      el.remove()
    }
  }
}

// Sibling helper for <link rel="canonical"> — same restore-on-unmount
// shape as setMeta, but link tags have a different attribute layout
// (rel + href instead of name/property + content), so keeping them
// separate is cleaner than a generic.
function setCanonical(href: string): () => void {
  let el = document.head.querySelector('link[rel="canonical"]') as
    | HTMLLinkElement
    | null
  const existed = el !== null
  const prevHref = el?.getAttribute('href') ?? null
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', 'canonical')
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
  return () => {
    if (!el) return
    if (existed) {
      if (prevHref !== null) el.setAttribute('href', prevHref)
      else el.removeAttribute('href')
    } else {
      el.remove()
    }
  }
}

export function useMeta(opts: MetaOptions): void {
  useEffect(() => {
    const cleanups: Array<() => void> = []

    if (opts.title) {
      const prevTitle = document.title
      document.title = opts.title
      cleanups.push(() => {
        document.title = prevTitle
      })
    }

    if (opts.description) {
      cleanups.push(setMeta('description', 'name', opts.description))
    }

    const ogT = opts.ogTitle ?? opts.title
    if (ogT) {
      cleanups.push(setMeta('og:title', 'property', ogT))
      cleanups.push(setMeta('twitter:title', 'name', ogT))
    }
    const ogD = opts.ogDescription ?? opts.description
    if (ogD) {
      cleanups.push(setMeta('og:description', 'property', ogD))
      cleanups.push(setMeta('twitter:description', 'name', ogD))
    }

    if (opts.canonical) {
      cleanups.push(setCanonical(opts.canonical))
    }

    return () => {
      // Run cleanups in reverse so meta tags created here are removed
      // before the title is restored — keeps the document in a clean
      // intermediate state.
      for (let i = cleanups.length - 1; i >= 0; i--) cleanups[i]()
    }
  }, [opts.title, opts.description, opts.ogTitle, opts.ogDescription, opts.canonical])
}
